import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ISimpleDisplayModel, SimpleDisplayModel, SimpleSearchComplete } from "@/Common/Components/Search/SimpleSearchAutocomplete";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocationModel } from "./Models/LocationModel";
import { RvqLocationPreference } from "./Models/RvqLocationPreference";
import { GetRvqLocationsBySearchResponse } from "./ResponseModels/GetRvqLocationsBySearchResponse";
import { GetRvqPreferencesResponse } from "./ResponseModels/GetRvqPreferencesResponse";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";
import { RvqAutoCompleteRow } from "./RvqAutoCompleteRow";

interface RvqLocationSelectionProps {
    selectedItem?: { Id: number, DisplayText: string };
    onChange: (id: number, displayText: string, preferences?: RvqLocationPreference[]) => void;
    onClear: () => void;
    onPreferencesUpdate: (preferences: RvqLocationPreference[], then?: () => void) => void;
    preferences: RvqLocationPreference[];
    isValid?: boolean;
    isRvqAuthenticated: boolean;
}

class RvqLocationSelection extends BaseReactComponent<RvqLocationSelectionProps, { searchText: string }> {
    private componentRef: SimpleSearchComplete<RvqLocationPreference>;

    constructor(props: RvqLocationSelectionProps) {
        super(props);
        this.state = { searchText: "" };
    }

    public render(): JSX.Element {
        const selectedItem = this.props.selectedItem;

        let locationList = QuebecVaccinationRegistryHelper.getSortedPreferenceList(this.props.preferences);
        return (
            <SimpleSearchComplete<RvqLocationPreference>
                ref={ref => this.componentRef = ref}
                componentId="immunizationLocation"
                selectedItemList={(selectedItem ? [selectedItem] : []).toImmutable()}
                onSelectionCallback={async ({ Item }) => {
                    let response: ClientResponse<GetRvqPreferencesResponse> = null;

                    if (!Item.Data.PreferenceId) {
                        response = await Client.postAsync<GetRvqPreferencesResponse>(
                            ServerOperations.Server.QuebecVaccinationRegistry_AddPreference,
                            {
                                LocationPreference: Item.Data
                            }
                        );
                    }

                    this.mutateState(
                        s => s.searchText = "",
                        () => this.props.onChange(Item.Id as number, Item.DisplayText, response?.data?.LocationPreferences.map(p => new RvqLocationPreference(p)))
                    );
                }}
                closeDropDownAfterSelection
                doNotEraseListAfterSelection
                useBlurEvent={false}
                value={this.state.searchText}
                onTextChange={searchString => this.mutateState(s => s.searchText = searchString)}
                valid={this.props.isValid ?? true}
                onSearch={async searchString => {
                    if (!IsNullOrWhiteSpace(searchString)) {
                        const filteredExistingPreferences = locationList.filter(x => x.Item.DisplayText.contains(searchString, { ignoreCase: true, ignoreDiacritics: true }));
                        const locations = (await this.handleLocationSearch(searchString))
                            ?.filter(newLocation => !filteredExistingPreferences.any(x => x.Item.Data.QuebecVaccinationRegistryId === newLocation.Id));
                        return [
                            ...filteredExistingPreferences,
                            ...locations.map(
                                (x, index) => new SimpleDisplayModel({
                                    Item: {
                                        Id: x.Id,
                                        DisplayText: LocationModel.getDisplayName(x),
                                        Data: LocationModel.toPreference(x)
                                    },
                                    Active: index === 0
                                })
                            )
                        ];
                    }
                    else {
                        return locationList;
                    }
                }}
                onClearCallback={() => this.props.onClear()}
                renderItem={(model: ISimpleDisplayModel<RvqLocationPreference>) => (
                    <RvqAutoCompleteRow
                        model={model}
                        onRvqPreferencesUpdate={(preferences, newPreference) => {
                            if (newPreference)
                                this.mutateState(
                                    s => s.searchText = "",
                                    () => this.props.onChange(newPreference.QuebecVaccinationRegistryId as number, newPreference.DisplayName, preferences)
                                );
                            else
                                this.props.onPreferencesUpdate(preferences, () => this.componentRef.executeSearch(this.state.searchText));
                        }}
                    />
                )}
            />
        );
    }

    private async handleLocationSearch(text: string): Promise<LocationModel[]> {
        const response = await Client.postAsync<GetRvqLocationsBySearchResponse>(
            this.props.isRvqAuthenticated
                ? ServerOperations.Server.QuebecVaccinationRegistry_GetLocationsBySearch
                : ServerOperations.Server.QuebecVaccinationRegistry_Offline_GetLocationsBySearch,
            {
                Name: text,
                CityName: null
            }
        );
        return response.data?.Locations?.map(p => new LocationModel(p)) ?? [];
    }
}

export { RvqLocationSelection };
